import { memo } from 'react';
import { IconButton, styled, Tooltip } from '@mui/material';
import SmartButton from '@mui/icons-material/SmartButton';
import type { CellContext } from '@tanstack/react-table';

import { ContextMenuButton } from '../ContextMenuButton';
import { useDeferredAction } from '../useDeferredAction';
import { type ContextMenuItem } from 'utils/hooks/userAgendaTable/useAgendaContextMenu';

const Wrapper = styled('div')(() => ({
  display: 'flex',
  gap: '4px',
  alignItems: 'center',
}));

interface Props<T, P> {
  row: CellContext<T, P>;
  contextMenuItems: ContextMenuItem[];
}

/** Used in both Helios and Flow */
const ButtonBase = <T, P>({ row, contextMenuItems }: Props<T, P>) => {
  const { onMouseDown, onClick, cancelDeferred } = useDeferredAction();
  const directItems = contextMenuItems.filter((item) => item.showButtonDirectly);
  const dropdownItems = contextMenuItems.filter((item) => !item.showButtonDirectly);

  return (
    <Wrapper>
      {directItems.map((item) => {
        const runAction = () => {
          item.changeBehavior((row.row.original as Record<string, string>)?.id);
        };

        return (
          <Tooltip key={`${row.row.id}-${item.name}`} title={item.name}>
            <IconButton
              aria-label={item.name}
              size="small"
              onMouseDown={(e) => onMouseDown(e, runAction)}
              onClick={(e) => onClick(e, runAction)}
              onMouseLeave={cancelDeferred}
              onPointerCancel={cancelDeferred}
              onDragStart={cancelDeferred}>
              <SmartButton fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      })}
      {dropdownItems.length > 0 && (
        <ContextMenuButton
          row={row}
          menuItems={dropdownItems.map((item) => ({ label: item.name, action: item.changeBehavior }))}
          onClick={(rowData, _, action) => {
            action?.((rowData as Record<string, string>)?.id);
          }}
        />
      )}
    </Wrapper>
  );
};

export const ButtonHelios = memo(ButtonBase) as typeof ButtonBase;
